import * as dateTimeFormat from '@formatjs/intl-datetimeformat/should-polyfill';
import * as numberFormat from '@formatjs/intl-numberformat/should-polyfill';
import * as relativeTimeFormat from '@formatjs/intl-relativetimeformat/should-polyfill';

/**
 * polyfill-force replaces the native Intl implementation globally.
 * When that happens we must load locale data for ALL supported locales,
 * not just the one that triggered the polyfill — otherwise the replaced
 * Intl falls back to the only loaded locale for every other locale.
 */

// Resolve a BCP47 locale to its @formatjs locale-data filename. We must do
// this BEFORE polyfill-force runs: once native Intl is replaced,
// shouldPolyfill may return undefined for locales whose data isn't loaded.
// Falls back to the language subtag when native Intl already supports the
// locale (shouldPolyfill returns undefined) — every supported locale here
// has a language-only data file in @formatjs.
function resolveDataFiles(locales: string[], shouldPolyfill: (l: string) => string | undefined): string[] {
  const files = locales.map(l => (shouldPolyfill(l) ?? l).split('-')[0]);
  return Array.from(new Set(files.filter((f): f is string => !!f)));
}
async function polyfillRelativeTimeFormatAll(locales: string[]) {
  const dataFiles = resolveDataFiles(locales, relativeTimeFormat.shouldPolyfill);
  const needsPolyfill = locales.some(l => relativeTimeFormat.shouldPolyfill(l));
  if (!needsPolyfill) return;
  await import('@formatjs/intl-relativetimeformat/polyfill-force');
  await Promise.all(dataFiles.map(f => import(`@formatjs/intl-relativetimeformat/locale-data/${f}`)));
}
async function polyfillNumberFormatAll(locales: string[]) {
  const dataFiles = resolveDataFiles(locales, numberFormat.shouldPolyfill);
  const needsPolyfill = locales.some(l => numberFormat.shouldPolyfill(l));
  if (!needsPolyfill) return;
  await import('@formatjs/intl-numberformat/polyfill-force');
  await Promise.all(dataFiles.map(f => import(`@formatjs/intl-numberformat/locale-data/${f}`)));
}
async function polyfillDateTimeFormatAll(locales: string[]) {
  const dataFiles = resolveDataFiles(locales, dateTimeFormat.shouldPolyfill);
  const needsPolyfill = locales.some(l => dateTimeFormat.shouldPolyfill(l));
  if (!needsPolyfill) return;
  await import('@formatjs/intl-datetimeformat/polyfill-force');
  await import('@formatjs/intl-datetimeformat/add-all-tz');
  await Promise.all(dataFiles.map(f => import(`@formatjs/intl-datetimeformat/locale-data/${f}`)));
}
async function polyfillAll(locales: string[]) {
  return Promise.all([polyfillRelativeTimeFormatAll(locales), polyfillNumberFormatAll(locales), polyfillDateTimeFormatAll(locales)]);
}

/**
 * Module-level promise that tracks polyfill readiness.
 * Must be started via `initPolyfills` and awaited via `awaitPolyfills`
 * before rendering date/number-formatted content.
 */
let _polyfillPromise: Promise<void> = Promise.resolve();

/** Kick off polyfill loading (call once at module init). */
export function initPolyfills(locales: string[]): void {
  _polyfillPromise = polyfillAll(locales).then(() => undefined).catch(err => {
    console.error('Failed to load Intl polyfills', err);
  });
}

/** Await polyfill completion — safe to call multiple times. */
export function awaitPolyfills(): Promise<void> {
  return _polyfillPromise;
}